import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { 
  Phone, Home, CheckCircle2, ArrowRight, AlertCircle, ChevronLeft, Landmark, PiggyBank
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AdsConsentCheckbox } from "@/components/AdsConsentCheckbox";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Toaster, toast } from "sonner";
import axios from 'axios';
import { getUtmParams } from '@/lib/attribution';
import { PrivacyContent } from '@/components/PrivacyContent';
import { ConsentBanner } from '@/components/ConsentBanner';
import { usePageTracking } from '@/lib/pageTracking';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const PHONE_DISPLAY = "078 79 01 15";
const PHONE_LINK = "+3278790115";

// Tracking functions
const trackCTAClick = (location, action) => {
  if (window.gtag) window.gtag('event', 'cta_click', { event_category: 'engagement', event_label: `${location}_${action}` });
};

const trackPhoneClick = (location) => {
  if (window.dataLayer) {
    window.dataLayer.push({ event: 'phone_click', event_category: 'Lead', event_action: 'Phone Click', event_label: location, click_url: `tel:${PHONE_LINK}`, click_text: PHONE_DISPLAY });
  }
  const utm = getUtmParams();
  const body = JSON.stringify({
    type: 'phone',
    source: location,
    page: window.location.pathname,
    user_agent: navigator.userAgent || '',
    keyword: utm.keyword || '',
    gclid: utm.gclid || '',
    landing_url: utm.landing_url || window.location.href,
    form_type: 'audit',
    ad_user_data_consent: utm.ad_user_data_consent,
  });
  const url = `${API}/track/click`;
  if (navigator.sendBeacon) {
    navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
  } else {
    fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
  }
};

const trackFormSubmit = (formType, callback) => {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'form_submit', event_category: 'Lead', event_action: 'Form Submit', event_label: 'Devis Audit', form_source: formType,
      eventCallback: () => { if (callback) callback(); }, eventTimeout: 2000
    });
  } else if (callback) callback();
};

// Privacy content
const SimulatorRenopack = () => {
  usePageTracking();
  const [step, setStep] = useState(1);
  const [showResult, setShowResult] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    labelPEB: '',
    revenus: '',
    montantTravaux: 40000,
    typeBien: '',
    nom: '', prenom: '', email: '', telephone: '', code_postal: '', consent_rgpd: false
  });

  // Catégories de revenus Rénopack
  const categories = {
    C1: { maxIncome: 28900, reduction: 50, taux: 0, label: "Revenus précaires" },
    C2: { maxIncome: 41100, reduction: 40, taux: 0, label: "Revenus modestes" },
    C3: { maxIncome: 67100, reduction: 15, taux: 0, label: "Revenus moyens" },
    C4: { maxIncome: 122800, reduction: 0, taux: "variable", label: "Revenus supérieurs" }
  };

  // Montants prédéfinis
  const montantOptions = [15000, 25000, 40000, 60000, 75000];
  
  // Taux bancaire moyen estimé (prêt travaux classique)
  const TAUX_BANQUE = 4.5;
  const DUREE_MOIS = 240; // 20 ans

  // Calcul de l'éligibilité et des avantages
  const calculateResults = () => {
    const cat = categories[formData.revenus];
    if (!cat) return null;
    
    const labelPEB = formData.labelPEB;
    const montant = formData.montantTravaux;
    
    // Vérification éligibilité PEB
    let eligible = false;
    let objectifPEB = '';
    if (labelPEB === 'G' || labelPEB === 'F') {
      eligible = true;
      objectifPEB = 'D';
    } else if (labelPEB === 'E') {
      eligible = true;
      objectifPEB = 'C';
    } else if (labelPEB === 'inconnu') {
      eligible = true;
      objectifPEB = '?';
    }
    
    const isRenopack = formData.revenus !== 'C4';
    const reduction = cat.reduction;
    const montantARembourser = montant * (1 - reduction / 100);
    const mensualiteRenopack = montantARembourser / DUREE_MOIS;
    const economieReduction = montant * (reduction / 100);
    
    // Calcul prêt bancaire classique
    const tauxMensuel = TAUX_BANQUE / 100 / 12;
    const mensualiteBanque = montant * (tauxMensuel * Math.pow(1 + tauxMensuel, DUREE_MOIS)) / (Math.pow(1 + tauxMensuel, DUREE_MOIS) - 1);
    const coutTotalBanque = mensualiteBanque * DUREE_MOIS;
    const interetsBanque = coutTotalBanque - montant;
    
    // Coût audit (estimé)
    const coutAudit = formData.typeBien === 'Appartement' ? 795 : 995;
    
    // Coût total Rénopack
    const coutTotalRenopack = montantARembourser + coutAudit;
    
    // Économie totale
    const economieTotale = coutTotalBanque - coutTotalRenopack;
    
    return {
      eligible,
      isRenopack,
      categorie: formData.revenus,
      categorieLabel: cat.label,
      reduction,
      labelPEB,
      objectifPEB,
      montant,
      montantARembourser,
      mensualiteRenopack,
      mensualiteBanque,
      coutTotalRenopack,
      coutTotalBanque,
      interetsBanque,
      coutAudit,
      economieReduction,
      economieTotale,
      taux: isRenopack ? '0%' : 'préférentiel'
    };
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.telephone) { toast.error("Veuillez renseigner votre email et téléphone"); return; }
    if (!formData.consent_rgpd) { toast.error("Veuillez accepter la politique de confidentialité"); return; }
    setIsSubmitting(true);
    try {
      const results = calculateResults();
      const utmParams = getUtmParams();
      const dataToSend = {
        nom: formData.nom,
        prenom: formData.prenom,
        email: formData.email,
        telephone: formData.telephone,
        code_postal: formData.code_postal,
        consent_rgpd: formData.consent_rgpd,
        consent_google_ads: !!formData.consent_google_ads,
        type_bien: formData.typeBien,
        source: 'simulateur-renopack',
        form_type: 'audit',
        current_page: '/simulateur',
        landing_url: utmParams.landing_url || window.location.href,
        ...utmParams,
        estimation: results ? `Catégorie ${results.categorie} (${results.categorieLabel}), Réduction ${results.reduction}%, Montant travaux ${results.montant}€, À rembourser ${Math.round(results.montantARembourser)}€` : ''
      };
      const res = await axios.post(`${API}/contact`, dataToSend);
      if (res.data.success) {
        trackFormSubmit('simulateur-renopack', () => {
          setShowContactForm(false);
          setShowThankYou(true);
        });
      }
    } catch { toast.error("Erreur", { description: `Veuillez réessayer ou appeler le ${PHONE_DISPLAY}` }); }
    finally { setIsSubmitting(false); }
  };

  const results = showResult ? calculateResults() : null;
  const formatMontant = (n) => new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);

  return (
    <div className="min-h-screen bg-gray-900" data-testid="simulator-page">
      <Helmet>
        <title>Simulateur Rénopack | Éligibilité Prêt 0% Wallonie</title>
        <meta name="description" content="Testez votre éligibilité au Rénopack : prêt à taux 0% avec jusqu'à 50% de réduction. Simulateur gratuit." />
      </Helmet>
      
      {/* Banner */}
      <div className="bg-gray-800 text-white py-2 px-4 text-sm">
        <div className="container mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 text-center">
          <div className="flex items-center gap-2">
            <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded">NOUVEAU</span>
            <span className="text-gray-300">Depuis le 1er octobre 2026 : <strong className="text-white">prêt à taux 0%</strong> pour vos travaux <strong className="text-yellow-400">avec un audit</strong></span>
          </div>
        </div>
      </div>
      
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <a href="/" className="flex items-center gap-2">
            <Home className="w-6 h-6 text-green-500" />
            <span className="font-bold text-white">Audits Logement</span>
          </a>
          <a href={`tel:${PHONE_LINK}`} onClick={() => trackPhoneClick('Simulator')} className="flex items-center gap-2 text-green-400 hover:text-green-300">
            <Phone className="w-4 h-4" />
            <span className="hidden sm:inline">{PHONE_DISPLAY}</span>
          </a>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-3xl">
        
        {/* Titre */}
        <div className="text-center mb-8">
          <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">
            Simulateur <span className="text-green-400">Rénopack</span>
          </h1>
          <p className="text-gray-400">Testez votre éligibilité au prêt à taux 0%</p>
        </div>

        {showThankYou ? (
          <div className="bg-green-600 rounded-2xl p-8 text-center text-white">
            <CheckCircle2 className="w-16 h-16 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Demande envoyée !</h2>
            <p className="mb-4">Un auditeur agréé vous contactera sous 24h.</p>
            <Button asChild className="bg-white text-green-600 hover:bg-gray-100">
              <a href="/">Retour à l'accueil</a>
            </Button>
          </div>
        ) : showContactForm ? (
          <div className="bg-gray-800 rounded-2xl p-6 lg:p-8">
            <h2 className="text-xl font-bold text-white mb-6 text-center">Parler avec un auditeur</h2>
            <form onSubmit={handleContactSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Prénom</Label>
                  <Input value={formData.prenom} onChange={(e) => setFormData({...formData, prenom: e.target.value})} className="bg-gray-700 border-gray-600 text-white" />
                </div>
                <div>
                  <Label className="text-gray-300">Nom</Label>
                  <Input value={formData.nom} onChange={(e) => setFormData({...formData, nom: e.target.value})} className="bg-gray-700 border-gray-600 text-white" />
                </div>
              </div>
              <div>
                <Label className="text-gray-300">Email *</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required className="bg-gray-700 border-gray-600 text-white" />
              </div>
              <div>
                <Label className="text-gray-300">Téléphone *</Label>
                <Input type="tel" value={formData.telephone} onChange={(e) => setFormData({...formData, telephone: e.target.value})} required className="bg-gray-700 border-gray-600 text-white" />
              </div>
              <div>
                <Label className="text-gray-300">Code postal</Label>
                <Input value={formData.code_postal} onChange={(e) => setFormData({...formData, code_postal: e.target.value})} className="bg-gray-700 border-gray-600 text-white" />
              </div>
              <div className="flex items-start gap-2">
                <Checkbox id="consent" checked={formData.consent_rgpd} onCheckedChange={(c) => setFormData({...formData, consent_rgpd: c})} className="h-5 w-5 mt-0.5 border-2 border-gray-300 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600 cursor-pointer flex-shrink-0" data-testid="checkbox-rgpd-simulator" />
                <Label htmlFor="consent" className="text-gray-400 text-sm">J'accepte la <button type="button" onClick={() => setPrivacyOpen(true)} className="text-green-400 underline">politique de confidentialité</button></Label>
              </div>
              <AdsConsentCheckbox id="ads-consent-simulator" dark checked={formData.consent_google_ads} onChange={(v) => setFormData({...formData, consent_google_ads: v})} />
              <Button type="submit" disabled={isSubmitting} className="w-full bg-green-500 hover:bg-green-600 h-12">
                {isSubmitting ? 'Envoi...' : 'Être rappelé par un auditeur'}
              </Button>
            </form>
          </div>
        ) : showResult && results ? (
          <div className="space-y-6">
            {/* Eligibility card */}
            <div className={`rounded-2xl p-6 lg:p-8 ${results.eligible ? 'bg-green-600' : 'bg-red-600'}`}>
              <div className="text-center text-white">
                {results.eligible ? (
                  <>
                    <CheckCircle2 className="w-16 h-16 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold mb-2">Vous êtes éligible au {results.isRenopack ? 'Rénopack' : 'Rénoprêt'} !</h2>
                    <p>Prêt à taux {results.taux} avec {results.reduction}% de réduction</p>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-16 h-16 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold mb-2">Non éligible au Rénopack</h2>
                    <p>Votre label PEB ({results.labelPEB}) ne correspond pas aux critères.</p>
                  </>
                )}
              </div>
            </div>

            {results.eligible && (
              <>
                {/* Comparaison côte à côte */}
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Rénopack */}
                  <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-2xl p-6 relative">
                    <div className="absolute top-0 right-0 bg-yellow-400 text-gray-900 text-xs font-bold px-3 py-1 rounded-bl-lg">
                      VOTRE CHOIX
                    </div>
                    <div className="flex items-center gap-3 mb-4">
                      <PiggyBank className="w-8 h-8 text-white" />
                      <div>
                        <h4 className="text-lg font-bold text-white">{results.isRenopack ? 'Rénopack' : 'Rénoprêt'}</h4>
                        <p className="text-green-200 text-xs">{results.isRenopack ? `Taux 0% + réduction ${results.reduction}% (écomatériaux compris)` : 'Taux 0% ou préférentiel selon votre profil'}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-2 text-white">
                      <div className="flex justify-between">
                        <span className="text-green-100 text-sm">Montant emprunté</span>
                        <span className="font-bold">{formatMontant(results.montant)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-green-100 text-sm">Réduction ({results.categorie})</span>
                        <span className="font-bold text-yellow-300">-{formatMontant(results.economieReduction)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-green-100 text-sm">Coût audit</span>
                        <span>{formatMontant(results.coutAudit)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-green-100 text-sm">Intérêts</span>
                        <span className="font-bold text-green-300">0 €</span>
                      </div>
                      <div className="border-t border-green-500 pt-2 flex justify-between">
                        <span className="text-green-100 text-sm">Mensualité</span>
                        <span className="font-bold text-xl">{formatMontant(results.mensualiteRenopack)}/mois</span>
                      </div>
                      <div className="bg-green-800/50 rounded-lg p-3 mt-2">
                        <div className="flex justify-between">
                          <span className="text-green-100 font-medium">Coût total</span>
                          <span className="font-bold text-2xl">{formatMontant(results.coutTotalRenopack)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Banque classique */}
                  <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
                    <div className="flex items-center gap-3 mb-4">
                      <Landmark className="w-8 h-8 text-gray-400" />
                      <div>
                        <h4 className="text-lg font-bold text-white">Prêt bancaire</h4>
                        <p className="text-gray-400 text-xs">Taux bancaire moyen (estimation) ~{TAUX_BANQUE}%</p>
                      </div>
                    </div>
                    
                    <div className="space-y-2 text-white">
                      <div className="flex justify-between">
                        <span className="text-gray-400 text-sm">Montant emprunté</span>
                        <span className="font-bold">{formatMontant(results.montant)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400 text-sm">Réduction</span>
                        <span className="text-gray-500">0 €</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400 text-sm">Coût audit</span>
                        <span className="text-gray-500">Non requis</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400 text-sm">Intérêts (20 ans)</span>
                        <span className="font-bold text-red-400">+{formatMontant(results.interetsBanque)}</span>
                      </div>
                      <div className="border-t border-gray-700 pt-2 flex justify-between">
                        <span className="text-gray-400 text-sm">Mensualité</span>
                        <span className="font-bold text-xl">{formatMontant(results.mensualiteBanque)}/mois</span>
                      </div>
                      <div className="bg-gray-700/50 rounded-lg p-3 mt-2">
                        <div className="flex justify-between">
                          <span className="text-gray-400 font-medium">Coût total</span>
                          <span className="font-bold text-2xl">{formatMontant(results.coutTotalBanque)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Économie totale */}
                <div className="bg-gradient-to-r from-yellow-500/20 to-green-500/20 border border-yellow-500/30 rounded-xl p-6 text-center">
                  <p className="text-yellow-400 font-medium mb-1">💰 Avec le {results.isRenopack ? 'Rénopack' : 'Rénoprêt'}, vous économisez</p>
                  <p className="text-4xl font-bold text-white">{formatMontant(results.economieTotale)}</p>
                  <p className="text-gray-400 text-sm mt-1">sur la durée totale du prêt (20 ans)</p>
                  {!results.isRenopack && (
                    <p className="text-gray-400 text-xs mt-2" data-testid="renopret-rate-disclaimer">Estimation à taux 0% : le taux réel du Rénoprêt (0% ou préférentiel) dépend de votre profil et de vos travaux.</p>
                  )}
                </div>
                
                {results.objectifPEB !== '?' && (
                  <div className="bg-amber-600/20 border border-amber-500/50 rounded-lg p-4">
                    <p className="text-amber-400 text-sm flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Objectif PEB requis : vos travaux doivent permettre d'atteindre au minimum le label <strong>{results.objectifPEB}</strong>
                    </p>
                  </div>
                )}

                {/* CTA Final */}
                <div className="bg-gray-800 rounded-2xl p-6 text-center">
                  <h3 className="text-lg font-bold text-white mb-2">Prêt à passer à l'action ?</h3>
                  <p className="text-gray-400 mb-4">L'audit logement est la première étape pour débloquer le Rénopack.</p>
                  <Button onClick={() => setShowContactForm(true)} size="lg" className="bg-green-500 hover:bg-green-600 h-14 px-8 text-lg">
                    <Phone className="w-5 h-5 mr-2" />
                    Parler avec un auditeur
                  </Button>
                </div>
              </>
            )}
            
            <Button variant="outline" onClick={() => { setShowResult(false); setStep(1); setFormData({...formData, labelPEB: '', revenus: '', montantTravaux: 40000}); }} className="w-full border-gray-600 text-gray-300 hover:bg-gray-800">
              Refaire une simulation
            </Button>
          </div>
        ) : (
          <div className="bg-gray-800 rounded-2xl p-6 lg:p-8">
            {/* Progress */}
            <div className="flex items-center justify-between mb-8">
              {[1, 2, 3, 4].map((s) => (
                <div key={s} className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${step >= s ? 'bg-green-500 text-white' : 'bg-gray-700 text-gray-400'}`}>
                    {s}
                  </div>
                  {s < 4 && <div className={`w-12 lg:w-20 h-1 mx-1 ${step > s ? 'bg-green-500' : 'bg-gray-700'}`} />}
                </div>
              ))}
            </div>

            {step === 1 && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-white text-center">Quel est le label PEB de votre logement ?</h2>
                <p className="text-gray-400 text-center text-sm">Le Rénopack est réservé aux logements E, F ou G</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {['E', 'F', 'G', 'inconnu'].map((label) => (
                    <button
                      key={label}
                      onClick={() => { setFormData({...formData, labelPEB: label}); setStep(2); }}
                      className={`p-6 rounded-xl border-2 transition-all ${
                        formData.labelPEB === label 
                          ? 'border-green-500 bg-green-500/20' 
                          : 'border-gray-600 hover:border-gray-500 bg-gray-700'
                      }`}
                    >
                      <span className={`text-3xl font-bold block ${label === 'inconnu' ? 'text-gray-400 text-sm' : ['E'].includes(label) ? 'text-yellow-400' : 'text-red-400'}`}>
                        {label === 'inconnu' ? '?' : label}
                      </span>
                      {label === 'inconnu' && <span className="text-gray-500 text-xs">Je ne sais pas</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-white text-center">Quelle est votre catégorie de revenus ?</h2>
                <p className="text-gray-400 text-center text-sm">Revenu globalement imposable - 5.000€/personne à charge. Réductions maximales, dont 5% de bonus écomatériaux.</p>
                <div className="grid gap-3">
                  {Object.entries(categories).map(([key, cat]) => (
                    <button
                      key={key}
                      onClick={() => { setFormData({...formData, revenus: key}); setStep(3); }}
                      className={`p-4 rounded-xl border-2 text-left transition-all flex items-center justify-between ${
                        formData.revenus === key 
                          ? 'border-green-500 bg-green-500/20' 
                          : 'border-gray-600 hover:border-gray-500 bg-gray-700'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <span className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${cat.reduction > 30 ? 'bg-green-500 text-white' : cat.reduction > 0 ? 'bg-green-600/50 text-green-300' : 'bg-gray-600 text-gray-400'}`}>
                          {key}
                        </span>
                        <div>
                          <span className="text-white font-medium block">{cat.label}</span>
                          <span className="text-gray-500 text-sm">≤ {cat.maxIncome.toLocaleString()}€/an</span>
                        </div>
                      </div>
                      <span className={`text-2xl font-bold ${cat.reduction > 0 ? 'text-green-400' : 'text-gray-500'}`}>
                        -{cat.reduction}%
                      </span>
                    </button>
                  ))}
                </div>
                <Button variant="ghost" onClick={() => setStep(1)} className="text-gray-400">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Retour
                </Button>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-white text-center">Quel type de bien ?</h2>
                <div className="grid grid-cols-2 gap-4">
                  {['Appartement', 'Maison'].map((type) => (
                    <button
                      key={type}
                      onClick={() => { setFormData({...formData, typeBien: type}); setStep(4); }}
                      className={`p-6 rounded-xl border-2 transition-all ${
                        formData.typeBien === type 
                          ? 'border-green-500 bg-green-500/20' 
                          : 'border-gray-600 hover:border-gray-500 bg-gray-700'
                      }`}
                    >
                      <span className="text-4xl mb-2 block">{type === 'Appartement' ? '🏢' : '🏠'}</span>
                      <span className="text-white font-bold">{type}</span>
                    </button>
                  ))}
                </div>
                <Button variant="ghost" onClick={() => setStep(2)} className="text-gray-400">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Retour
                </Button>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-white text-center">Quel montant souhaitez-vous emprunter ?</h2>
                <p className="text-gray-400 text-center text-sm">Pour vos travaux de rénovation énergétique</p>
                
                {/* Boutons montants prédéfinis */}
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                  {montantOptions.map((m) => (
                    <button
                      key={m}
                      onClick={() => setFormData({...formData, montantTravaux: m})}
                      className={`py-3 px-2 rounded-xl border-2 transition-all text-center ${
                        formData.montantTravaux === m 
                          ? 'border-green-500 bg-green-500/20' 
                          : 'border-gray-600 hover:border-gray-500 bg-gray-700'
                      }`}
                    >
                      <span className={`font-bold ${formData.montantTravaux === m ? 'text-green-400' : 'text-white'}`}>
                        {(m / 1000)}k€
                      </span>
                    </button>
                  ))}
                </div>
                
                {/* Slider pour ajustement fin */}
                <div className="bg-gray-700 rounded-xl p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-400 text-sm">Ajustez le montant</span>
                    <span className="text-2xl font-bold text-green-400">{formData.montantTravaux.toLocaleString()}€</span>
                  </div>
                  <input 
                    type="range" 
                    min="5000" 
                    max="75000" 
                    step="1000"
                    value={formData.montantTravaux}
                    onChange={(e) => setFormData({...formData, montantTravaux: Number(e.target.value)})}
                    className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-green-500"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>5.000€</span>
                    <span>75.000€</span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button variant="ghost" onClick={() => setStep(3)} className="text-gray-400">
                    <ChevronLeft className="w-4 h-4 mr-1" /> Retour
                  </Button>
                  <Button onClick={() => setShowResult(true)} className="flex-1 bg-green-500 hover:bg-green-600 h-12">
                    Voir ma simulation
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-gray-400 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm">
          <p>© {new Date().getFullYear()} Audits Logement Wallonie — Service de mise en relation gratuit</p>
          <div className="flex justify-center gap-4 mt-2">
            <a href="/" className="hover:text-white">Accueil</a>
            <a href="/certificat-peb" className="hover:text-white">Certificat PEB</a>
            <button onClick={() => setPrivacyOpen(true)} className="hover:text-white">Confidentialité</button>
          </div>
        </div>
      </footer>

      <Dialog open={privacyOpen} onOpenChange={setPrivacyOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <PrivacyContent />
        </DialogContent>
      </Dialog>
      <ConsentBanner />
      <Toaster position="top-right" richColors />
    </div>
  );
};

export default SimulatorRenopack;
